/**
 * AddRunnersModal Component
 * Search modal to find and follow runners by bib or name
 */

import { useState, useMemo } from 'react';
import type { RunnerSummary } from '../../lib/types';
import { RunnerAvatarV2 } from './RunnerAvatarV2';

interface AddRunnersModalProps {
  runners: RunnerSummary[];
  followedBibs: Set<string>;
  onToggleFollow: (bib: string) => void;
  onClose: () => void;
}

export function AddRunnersModal({
  runners,
  followedBibs,
  onToggleFollow,
  onClose,
}: AddRunnersModalProps) {
  const [search, setSearch] = useState('');

  const filteredRunners = useMemo(() => {
    const query = search.trim().toLowerCase();
    
    // Always filter out anonymous runners (both default and search views)
    const nonAnonymousRunners = runners.filter((r) => !r.is_anonymous);
    
    if (!query) return nonAnonymousRunners.slice(0, 20); // Show first 20 non-anonymous runners by default

    return nonAnonymousRunners.filter((r) => {
      const fullName = `${r.first_name} ${r.last_name}`.toLowerCase();
      return r.bib.toLowerCase().includes(query) || fullName.includes(query);
    }).slice(0, 50);
  }, [runners, search]);

  return (
    <div className="v2-modal-backdrop" onClick={onClose}>
      <div className="v2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="v2-modal-header">
          <span className="v2-modal-title">Add Runners</span>
          <button className="v2-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="v2-modal-body">
          <input
            type="text"
            className="v2-modal-search"
            placeholder="Search by name or bib number..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {filteredRunners.map((runner) => (
            <div
              key={runner.id}
              className="v2-modal-runner-row"
              onClick={() => onToggleFollow(runner.bib)}
            >
              <RunnerAvatarV2
                firstName={runner.first_name}
                lastName={runner.last_name}
                size="sm"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--v2-text-dark)' }}>
                  {runner.first_name} {runner.last_name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                  BIB {runner.bib}
                  {runner.gender && ` · ${runner.gender}`}
                </div>
              </div>
              <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: followedBibs.has(runner.bib)
                  ? '2px solid var(--v2-accent-primary)'
                  : '2px solid var(--v2-border-light)',
                background: followedBibs.has(runner.bib) ? 'var(--v2-accent-primary)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {followedBibs.has(runner.bib) && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            </div>
          ))}
          {filteredRunners.length === 0 && (
            <p style={{ textAlign: 'center', padding: 24, color: 'var(--v2-text-muted)', fontSize: 13 }}>
              No runners found matching "{search}"
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
